// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useState } from "react"
import { Link } from "react-router-dom"
import {
  useSubmitCreditRequest,
  getCreditRequestErrorMessage,
} from "@/queries/queries.creditRequests"

// The six wire values the Backend's `RequesterProfile` enum parses
// case-insensitively — narrower than the generated
// `SubmitCreditRequestInput.profile` (a bare `string`) because only this
// fixed set renders as select options.
type CreditRequestProfile =
  | "individual"
  | "student"
  | "researcher"
  | "startup"
  | "company"
  | "oss"

const PROFILE_OPTIONS: { value: CreditRequestProfile; label: string }[] = [
  { value: "individual", label: "Individual" },
  { value: "student", label: "Student" },
  { value: "researcher", label: "Researcher" },
  { value: "startup", label: "Startup" },
  { value: "company", label: "Company" },
  { value: "oss", label: "Open-source" },
]

type FieldErrors = Partial<{
  useCase: string
  requestedAmount: string
  profile: string
}>

const validate = (fields: {
  useCase: string
  requestedAmount: string
  profile: CreditRequestProfile | ""
}): FieldErrors => {
  const errors: FieldErrors = {}
  if (!fields.useCase.trim()) {
    errors.useCase = "Use case is required."
  }
  const amount = Number(fields.requestedAmount)
  if (
    fields.requestedAmount.trim() === "" ||
    !Number.isFinite(amount) ||
    amount <= 0
  ) {
    errors.requestedAmount = "Enter an amount greater than zero."
  }
  if (!fields.profile) {
    errors.profile = "Choose a profile."
  }
  return errors
}

const ConfirmationScreen = () => (
  <div className="flex flex-col gap-3">
    <h1 className="text-[22px] font-semibold text-foreground">
      Request received
    </h1>
    <p role="status" className="text-[13.5px] text-text-secondary">
      Your request was received — we&apos;ll get back to you within 15 days
      max.
    </p>
    <Link to="/profile" className="text-primary text-[13px] w-fit">
      Back to my account
    </Link>
  </div>
)

export default function RequestCreditsView() {
  const [useCase, setUseCase] = useState("")
  const [requestedAmount, setRequestedAmount] = useState("")
  const [profile, setProfile] = useState<CreditRequestProfile | "">("")
  const [currentUsage, setCurrentUsage] = useState("")
  const [errors, setErrors] = useState<FieldErrors>({})

  const mutation = useSubmitCreditRequest()

  if (mutation.isSuccess) {
    return <ConfirmationScreen />
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const nextErrors = validate({ useCase, requestedAmount, profile })
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    mutation.mutate({
      use_case: useCase.trim(),
      requested_amount: Number(requestedAmount),
      profile: profile as CreditRequestProfile,
      current_usage: currentUsage.trim(),
    })
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-5 max-w-[520px]"
    >
      <div className="flex flex-col gap-[7px] border-b border-border pb-5">
        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-text-tertiary">
          Credits
        </span>
        <h1 className="text-[22px] font-semibold text-foreground">
          Request more credits
        </h1>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] text-text-tertiary">
          Envisaged use case
        </span>
        <textarea
          aria-label="Envisaged use case"
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
          className="min-h-[90px] rounded-[10px] border border-border-strong bg-[rgba(4,20,24,0.5)] px-3 py-2 text-[13px] text-foreground"
        />
        {errors.useCase && <span role="alert">{errors.useCase}</span>}
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] text-text-tertiary">
          Requested amount
        </span>
        <input
          aria-label="Requested amount"
          type="number"
          value={requestedAmount}
          onChange={(e) => setRequestedAmount(e.target.value)}
          className="h-9 rounded-[10px] border border-border-strong bg-[rgba(4,20,24,0.5)] px-3 text-[13px] text-foreground"
        />
        {errors.requestedAmount && (
          <span role="alert">{errors.requestedAmount}</span>
        )}
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] text-text-tertiary">Profile</span>
        <select
          aria-label="Profile"
          value={profile}
          onChange={(e) =>
            setProfile(e.target.value as CreditRequestProfile | "")
          }
          className="h-9 rounded-[10px] border border-border-strong bg-[rgba(4,20,24,0.5)] px-3 text-[13px] text-foreground"
        >
          <option value="">Select a profile…</option>
          {PROFILE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errors.profile && <span role="alert">{errors.profile}</span>}
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] text-text-tertiary">
          Current usage
        </span>
        <textarea
          aria-label="Current usage"
          value={currentUsage}
          onChange={(e) => setCurrentUsage(e.target.value)}
          className="min-h-[70px] rounded-[10px] border border-border-strong bg-[rgba(4,20,24,0.5)] px-3 py-2 text-[13px] text-foreground"
        />
      </label>

      {mutation.isError && (
        <div role="alert" className="text-[12.5px] text-destructive">
          {getCreditRequestErrorMessage(mutation.error)}
        </div>
      )}

      <button
        type="submit"
        disabled={mutation.isPending}
        className="inline-flex h-[42px] w-fit items-center justify-center rounded-[11px] px-5 text-[13.5px] font-semibold disabled:cursor-not-allowed disabled:opacity-50"
        style={{
          background: "linear-gradient(135deg, #45F0C8, #16C2A0)",
          color: "#04141A",
        }}
      >
        {mutation.isPending ? "Submitting…" : "Submit request"}
      </button>
    </form>
  )
}
